import { TableCell, TableRow } from "@mui/material";
import OrderPreviewTableLayout, { OrderPreviewColumn, PreviewNumber } from "src/Components/OrderPreviewTableLayout";
import {
  CoveredMatrixCriterionType,
  CoveredMatrixExecutionMethod,
  CoveredMatrixOrderPreviewResult,
  CoveredMatrixVolumeCalculationType,
} from "src/Interfaces/coveredMatrixOrder";
import { formatCriterionValue } from "./model";

const executionNames: Record<number, string> = {
  [CoveredMatrixExecutionMethod.BothSides]: "ارسال هر دو سمت",
  [CoveredMatrixExecutionMethod.BaseAlreadyOwned]: "موجود بودن نماد پایه",
  [CoveredMatrixExecutionMethod.FutureBasePurchase]: "قیمت ثابت نماد پایه",
};
const volumeNames: Record<number, string> = {
  [CoveredMatrixVolumeCalculationType.FixedAmount]: "مقدار ثابت",
  [CoveredMatrixVolumeCalculationType.AssetPercent]: "% از دارایی نماد پایه",
  [CoveredMatrixVolumeCalculationType.CoveredCallImbalancePercent]: "% از ناترازی گروه پرتفو کاوردکال",
};
const criterionNames: Record<number, string> = {
  [CoveredMatrixCriterionType.MinDailyReturn]: "حداقل بازده روزانه (%)",
  [CoveredMatrixCriterionType.MaxCost]: "حداکثر بهای تمام شده",
  [CoveredMatrixCriterionType.FixedCallPrice]: "حداقل قیمت کال",
};
const sendNames: Record<number, string> = { 0: "خودکار", 1: "اعتباری", 2: "غیر اعتباری" };

const columns: OrderPreviewColumn[] = [
  { key: "row", label: "#", width: 30 },
  { key: "account", label: "نام اکانت", width: 95 },
  { key: "base", label: "نماد پایه", width: 80 },
  { key: "execution", label: <>روش اجرا /<br />قیمت مبنای پایه</>, width: 105 },
  { key: "method", label: <>روش تعیین<br />حجم</>, width: 105 },
  { key: "input", label: <>مقدار<br />ورودی حجم</>, width: 70 },
  { key: "holding", label: <>موجودی<br />نماد پایه</>, width: 85 },
  { key: "imbalance", label: <>ناترازی گروه<br />پرتفو کاوردکال</>, width: 85 },
  { key: "target", label: <>حجم محاسبه‌شده<br />(دارایی پایه)</>, width: 90 },
  { key: "option", label: <>نماد<br />سمت دوم</>, width: 95 },
  { key: "criterion", label: "نوع معیار", width: 100 },
  { key: "criterionValue", label: <>مقدار<br />معیار</>, width: 75 },
  { key: "volume", label: <>حجم معادل<br />(قرارداد)</>, width: 75 },
  { key: "send", label: <>ترتیب<br />ارسال</>, width: 65 },
];

const OrderPreview = ({ preview, baseReferencePrice }: {
  preview: CoveredMatrixOrderPreviewResult;
  baseReferencePrice?: number | null;
}) => {
  const warnings = [
    ...(preview.warnings || []),
    ...preview.rows.flatMap((row) => row.items.filter((item) => !item.willCreateOrder).map((item) =>
      `${row.accountName} · ${item.optionName || item.optionIsin}: حجم کمتر از یک قرارداد`)),
  ];
  return <div className="flex w-full min-w-0 flex-col gap-4" dir="rtl">
    {preview.errors.length ? <ul className="list-inside list-disc rounded-lg border border-orange-200 bg-orange-50 p-3 text-xs leading-6 text-orange-900">
      {preview.errors.map((error, index) => <li key={index}>{error}</li>)}
    </ul> : null}
    {warnings.length ? <ul className="list-inside list-disc rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs leading-6 text-amber-900">
      {warnings.map((warning, index) => <li key={index}>{warning}</li>)}
    </ul> : null}
    <p className="text-xs text-slate-500">فرایند: ورود · نوع جفت: کاوردکال</p>
    <OrderPreviewTableLayout title="پیش‌نمایش کاوردکال ماتریسی" count={preview.rows.length} columns={columns} minWidth={1100}>
      <>{preview.rows.flatMap((row, rowIndex) => {
        const items = row.items.length ? row.items : [null];
        return items.map((item, itemIndex) => <TableRow key={`${row.accountId}-${row.baseIsin}-${item?.optionIsin || "empty"}`}>
          {itemIndex === 0 ? <>
            <TableCell rowSpan={items.length}>{rowIndex + 1}</TableCell>
            <TableCell rowSpan={items.length}>{row.accountName}<div className="order-preview-secondary">{row.items.length} نماد سمت دوم</div></TableCell>
            <TableCell rowSpan={items.length}>{row.baseSymbolName || row.baseIsin}</TableCell>
            <TableCell rowSpan={items.length}>
              <div>{executionNames[row.executionMethod] || "—"}</div>
              {row.executionMethod === CoveredMatrixExecutionMethod.FutureBasePurchase ? <div className="order-preview-secondary"><PreviewNumber value={row.manualBaseReferencePrice ?? baseReferencePrice} /></div> : null}
            </TableCell>
            <TableCell rowSpan={items.length}>{volumeNames[row.volumeCalculationType] || "—"}</TableCell>
            <TableCell rowSpan={items.length}><PreviewNumber value={row.volumeInput} />{row.volumeCalculationType !== CoveredMatrixVolumeCalculationType.FixedAmount ? <div className="order-preview-secondary">درصد (%)</div> : <div className="order-preview-secondary">دارایی پایه</div>}</TableCell>
            <TableCell rowSpan={items.length}><PreviewNumber value={row.baseHoldingVolume} /></TableCell>
            <TableCell rowSpan={items.length}><PreviewNumber value={row.portfolioCoveredCallImbalance} /></TableCell>
            <TableCell rowSpan={items.length}><PreviewNumber value={row.targetVolume} /></TableCell>
          </> : null}
          <TableCell>{item ? item.optionName || item.optionIsin : "—"}</TableCell>
          <TableCell>{item ? criterionNames[item.criterionType] || "—" : "—"}</TableCell>
          <TableCell><span dir="ltr" className="order-preview-number">{item ? formatCriterionValue(item.criterionValue, item.criterionType) : "—"}</span></TableCell>
          <TableCell>
            <PreviewNumber value={item?.optionEquivalentVolume} />
          </TableCell>
          {itemIndex === 0 ? <TableCell rowSpan={items.length}>{sendNames[row.sendOrderType] || "—"}</TableCell> : null}
        </TableRow>);
      })}</>
    </OrderPreviewTableLayout>
  </div>;
};

export default OrderPreview;
